'use client';

import * as React from 'react';
import { Download, Plus, ShoppingCart, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { TableCard } from '@/components/ui/table-card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useProductsList } from '@/lib/api/products';
import { useIntegrationsList } from '@/lib/api/integrations';
import { ProductsTable } from './components/ProductsTable';
import { Link } from 'react-router-dom';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useTablePage } from '@/hooks/use-table-page';
import {
  productsFilterMapping,
  productsSortMapping,
  productsQueryParamsMapping,
} from './utils/product-table-mapper';
import { DemoSeedingProgress } from '@/components/onboarding';
import { trpc } from '@/lib/api/trpc';
import { useStore } from '@/lib/api/stores';
import { isDemoSeedingEnabled } from '@/config/branding';

/** Storefront provider IDs that support product sync */
const STOREFRONT_PROVIDERS = ['shopify', 'woocommerce'] as const;

/**
 * Store details page - Products section
 * Displays products for a specific store
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(StoreDetailsPage, PERMISSIONS.STORES_VIEW);

function StoreDetailsPage() {
  const { t } = useTranslation(['stores', 'common']);
  const { id, storeRoutes } = useStoreNavigate();

  // Fetch store to get the display currency (first charge currency)
  const { data: store, isPending: storeLoading } = useStore(id!);
  const displayCurrency = store?.chargeCurrencies?.[0];

  // Check for demo seeding status (only for TagadaPay brand)
  const demoSeedingEnabled = isDemoSeedingEnabled();
  const { data: seedingStatus, isLoading: seedingStatusLoading } = trpc.stores.getDemoSeedingStatus.useQuery(
    { storeId: id! },
    { enabled: !!id && demoSeedingEnabled },
  );

  // Check if demo seeding is in progress or recently completed
  const showSeedingProgress = React.useMemo(() => {
    if (!seedingStatus) return false;
    // Show if any task is pending or if all completed within the last minute
    const hasPending = seedingStatus.products === 'pending' ||
      seedingStatus.paymentFlow === 'pending' ||
      seedingStatus.funnel === 'pending';
    const isAllCompleted = seedingStatus.products === 'completed' &&
      seedingStatus.paymentFlow === 'completed' &&
      seedingStatus.funnel === 'completed';
    if (hasPending) return true;
    // Show completed state for a short time
    if (isAllCompleted && seedingStatus.completedAt) {
      const completedAt = new Date(seedingStatus.completedAt);
      const minuteAgo = new Date(Date.now() - 60 * 1000);
      return completedAt > minuteAgo;
    }
    // Show if there are failures
    return seedingStatus.products === 'failed' ||
      seedingStatus.paymentFlow === 'failed' ||
      seedingStatus.funnel === 'failed';
  }, [seedingStatus]);

  // Check if storefront integration is connected
  const { data: integrations, isLoading: integrationsLoading } = useIntegrationsList(id);

  const hasStorefrontIntegration = React.useMemo(() => {
    return integrations?.some(
      (integration) =>
        STOREFRONT_PROVIDERS.includes(integration.provider as typeof STOREFRONT_PROVIDERS[number]) &&
        integration.enabled
    );
  }, [integrations]);

  // Table state managed by ProductsTable
  const { setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: productsFilterMapping,
    sortMapping: productsSortMapping,
    queryParamsMapping: productsQueryParamsMapping,
  });

  // Use API hook to fetch products
  const {
    data: productsResponse,
    isLoading: loading,
    error,
    refetch,
  } = useProductsList({
    storeId: id,
    page: queryInput.pagination.page - 1,
    per_page: queryInput.pagination.pageSize,
    sort: queryInput.sortBy.field,
    sort_order: queryInput.sortBy.direction,
    filters: queryInput.filters,
  });

  const products = productsResponse?.items || [];
  const total = productsResponse?.pagination.totalItems || 0;

  // Auto-refetch products when products seeding completes
  const handleProductsSeeded = React.useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{t('stores:products')}</h1>
          <p className="text-muted-foreground text-sm">{t('stores:manageProductsDescription')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="w-full sm:w-auto">
            <Download className="size-4" />
            {t('stores:export')}
          </Button>
          <Link to={storeRoutes.PRODUCT_CREATE()}>
            <Button className="w-full sm:w-auto">
              <Plus className="size-4" />
              {t('stores:addProduct')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Demo seeding progress banner */}
      {showSeedingProgress && id && (
        <DemoSeedingProgress
          storeId={id}
          storeRoutes={storeRoutes}
          onProductsComplete={handleProductsSeeded}
        />
      )}

      {/* Banner to connect storefront if not connected */}
      {!integrationsLoading && !hasStorefrontIntegration && !showSeedingProgress && (
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50">
          <ShoppingCart className="size-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-blue-800 dark:text-blue-200">
              {t('stores:syncFromStorefrontHint')}
            </span>
            <Link to={`${storeRoutes.INTEGRATIONS()}?category=storefront`}>
              <Button variant="outline" size="sm" className="whitespace-nowrap border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900">
                {t('stores:connectStorefront')}
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats cards */}
      {/* <ResourceStatsCardGrid cards={statsCards} loading={loading} /> */}

      {/* Products table */}
      <TableCard>
        <ProductsTable
          products={products}
          total={total}
          loading={loading || storeLoading}
          onTableStateChange={setTableState}
          onRefetch={refetch}
          displayCurrency={displayCurrency}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('stores:loadProductsError')}
          </div>
        )}
      </TableCard>
    </div>
  );
}
